import { useEffect } from "react";
import { NavLink, Link, Route, Routes, useNavigate } from "react-router-dom";
import {
  FileText, Home as HomeIcon, Inbox, LayoutDashboard, Library,
  LogOut, Newspaper, Settings, ExternalLink, HelpCircle, ImageIcon,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useSeo } from "../lib/seo";
import Login, { ResetPassword } from "./Login";
import Dashboard from "./Dashboard";
import AdminArticles from "./AdminArticles";
import ArticleEditor from "./ArticleEditor";
import AdminBooks from "./AdminBooks";
import AdminPages from "./AdminPages";
import AdminInbox from "./AdminInbox";
import AdminNewsletter from "./AdminNewsletter";
import AdminMedia from "./AdminMedia";
import AdminSettings from "./AdminSettings";
import AdminGuide from "./AdminGuide";
import AdminPreview from "./AdminPreview";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-menu-dashboard" },
  { to: "/admin/tulisan", label: "Tulisan", icon: FileText, testid: "admin-menu-tulisan" },
  { to: "/admin/buku", label: "Buku & Karya", icon: Library, testid: "admin-menu-buku" },
  { to: "/admin/halaman", label: "Halaman", icon: HomeIcon, testid: "admin-menu-halaman" },
  { to: "/admin/pesan", label: "Konsultasi & Pesan", icon: Inbox, testid: "admin-menu-pesan" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Newspaper, testid: "admin-menu-newsletter" },
  { to: "/admin/media", label: "Media", icon: ImageIcon, testid: "admin-menu-media" },
  { to: "/admin/pengaturan", label: "Pengaturan", icon: Settings, testid: "admin-menu-pengaturan" },
  { to: "/admin/panduan", label: "Panduan", icon: HelpCircle, testid: "admin-menu-panduan" },
];

export default function AdminApp() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useSeo({ title: "Admin — Heri Sensei", noindex: true });

  useEffect(() => {
    document.title = "Admin — Heri Sensei";
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <p className="text-sub text-sm">Memeriksa sesi…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-app">
      <Routes>
        <Route path="reset-kata-sandi" element={<ResetPassword />} />
        <Route
          path="*"
          element={
            user ? (
              <div className="flex min-h-screen">
                <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-line bg-surface p-4 sticky top-0 h-screen">
                  <Link to="/admin" className="font-serif text-xl text-ink px-3 py-2 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-enji inline-block" aria-hidden="true" />
                    Admin
                  </Link>
                  <nav className="mt-6 flex flex-col gap-1" aria-label="Menu admin">
                    {MENU.map(({ to, label, icon: Icon, end, testid }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={end}
                        data-testid={testid}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium transition-colors ${isActive ? "bg-enji-strong text-white" : "text-sub hover:text-ink hover:bg-surface-muted"}`
                        }
                      >
                        <Icon size={16} /> {label}
                      </NavLink>
                    ))}
                  </nav>
                  <div className="mt-auto space-y-1 pt-6 border-t border-line">
                    <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium text-sub hover:text-ink">
                      <ExternalLink size={16} /> Lihat Situs
                    </Link>
                    <button
                      data-testid="admin-logout-button"
                      onClick={async () => {
                        await logout();
                        navigate("/admin");
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium text-sub hover:text-enji w-full"
                    >
                      <LogOut size={16} /> Keluar
                    </button>
                  </div>
                </aside>

                <div className="flex-1 min-w-0">
                  <div className="md:hidden sticky top-0 z-30 bg-ivory/95 backdrop-blur border-b border-line px-4 h-14 flex items-center justify-between">
                    <Link to="/admin" className="font-serif text-lg text-ink">Admin</Link>
                    <div className="flex gap-1 overflow-x-auto no-scrollbar" data-testid="admin-mobile-nav">
                      {MENU.map(({ to, label, end, testid }) => (
                        <NavLink key={to} to={to} end={end} data-testid={`mobile-${testid}`} className={({ isActive }) => `px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap ${isActive ? "bg-enji text-white" : "text-sub"}`}>
                          {label.split(" ")[0]}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 sm:p-8 max-w-5xl mx-auto">
                    <Routes>
                      <Route index element={<Dashboard />} />
                      <Route path="tulisan" element={<AdminArticles />} />
                      <Route path="tulisan/baru" element={<ArticleEditor />} />
                      <Route path="tulisan/:id" element={<ArticleEditor />} />
                      <Route path="buku" element={<AdminBooks />} />
                      <Route path="buku/baru" element={<AdminBooks editorOpen />} />
                      <Route path="buku/:id" element={<AdminBooks editorOpen />} />
                      <Route path="halaman" element={<AdminPages />} />
                      <Route path="pesan" element={<AdminInbox />} />
                      <Route path="newsletter" element={<AdminNewsletter />} />
                      <Route path="media" element={<AdminMedia />} />
                      <Route path="pengaturan" element={<AdminSettings />} />
                      <Route path="panduan" element={<AdminGuide />} />
                      <Route path="pratinjau/:id" element={<AdminPreview />} />
                    </Routes>
                  </div>
                </div>
              </div>
            ) : (
              <Login />
            )
          }
        />
      </Routes>
    </div>
  );
}
